/**
 * ActionRecords — the structured, per-node stream of discrete actions a flow
 * node took while running (flow-visualizer-v2 FR-1). Where {@link NodeTraceEntry}
 * (runTrace.ts) is the free-text chat transcript, an ActionRecord is a typed
 * row: "Edit src/foo.ts", "Bash npm test", "→ chose ship", "FAILED: …". The
 * inspector drill-down (WU-2), live bubble (WU-3) and log view (WU-4) all read
 * these; the header rollup (WU-6) counts them.
 *
 * Ownership split (mirrors runTrace's append/persist split):
 *   - {@link classifyThinkerEvent} is PURE — it turns one thinker/tool event
 *     into the record's *content* (an {@link ActionDraft}: kind/tool/summary/
 *     detail/file), with no run context. Trivially unit-testable.
 *   - The host (FlowRunPanel.postAction) stamps the run context — the run-global
 *     monotonic `seq`, `at`, and `nodeId` — turning a draft into a full
 *     {@link ActionRecord}, then streams it to the webview and persists it.
 *
 * The extension host keeps a byte-for-byte logic mirror of this module at
 * thinkinghub-vscode/src/flows/actionLog.ts (the host `src/` tsc pass doesn't
 * import this webview-only package — same reason as the runTrace.ts mirror).
 * Keep the two in sync; the caps + classifier + helpers must match so live,
 * persisted and replayed state converge. The extension test suite
 * (src/test/actionLog.test.ts) exercises the host mirror.
 */

/**
 * The kind of action a record captures (FR-1.1). `edit`/`read`/`command` are
 * refinements of `tool` for file-writing / file-reading / shell tools; the
 * non-tool kinds are emitted by the executor's control flow, not the thinker.
 */
export type ActionKind =
  | "tool"
  | "edit"
  | "read"
  | "command"
  | "thinking-marker"
  | "transition"
  | "error"
  | "approval"
  | "result";

/** One structured action of a node's run (FR-1.1). */
export interface ActionRecord {
  /** Run-global monotonic ordinal (stamped by the host on ingest). */
  seq: number;
  /** Wall-clock time the action was recorded. */
  at: number;
  /** The node that took the action. */
  nodeId: string;
  kind: ActionKind;
  /** Tool name for tool-family records (Bash, Edit, Read, Skill, …). */
  tool?: string;
  /** One-line label (capped {@link MAX_ACTION_SUMMARY_CHARS}). */
  summary: string;
  /** Args/result/command preview (capped {@link MAX_ACTION_DETAIL_CHARS}). */
  detail?: string;
  /** Absolute/relative path touched, for edit/read records (FR-1.4). */
  file?: string;
  /** Duration in ms when known (e.g. a completed node's total time). */
  durationMs?: number;
}

/**
 * A record's content without its run context — what {@link classifyThinkerEvent}
 * produces and the host completes into an {@link ActionRecord} (adding
 * seq/at/nodeId). Callers building non-thinker records (transition/error/result)
 * pass drafts of the same shape.
 */
export type ActionDraft = Omit<ActionRecord, "seq" | "at" | "nodeId">;

/**
 * The minimal shape of a thinker/tool event as it arrives on
 * `FlowExecutionEvent.node_event.event` (mirrors IdeThinkerEvent). Only the
 * fields the classifier reads are declared so the pure module stays host-free.
 */
export interface ThinkerEventLike {
  type: string;
  text?: string;
  metadata?: Record<string, unknown>;
}

/** `detail` (args/result preview) budget — ~1KB (FR-1.2). */
export const MAX_ACTION_DETAIL_CHARS = 1024;
/** `summary` one-line budget. */
export const MAX_ACTION_SUMMARY_CHARS = 200;
/** Records kept per node — oldest drop first (FR-1.2). */
export const MAX_ACTIONS_PER_NODE = 500;
/** Records kept across the whole run (FR-1.2). */
export const MAX_ACTIONS_PER_RUN = 2000;
/** Files-in-progress shown for the running node (FR-1.4). */
export const MAX_FILES_IN_PROGRESS = 8;

const ELLIPSIS = "…";

/** Truncate to `max` chars with a trailing ellipsis marker. */
function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + ELLIPSIS : s;
}

/** Last path segment (posix or windows separators). */
function basename(p: string): string {
  const parts = p.split(/[\\/]/);
  return parts[parts.length - 1] || p;
}

/** Tools whose primary argument is a file path we should surface as `file`. */
const EDIT_TOOLS = new Set(["Write", "Edit", "MultiEdit", "NotebookEdit"]);

/** Map a tool name to the record kind (FR-1.1). */
function toolKind(tool: string): ActionKind {
  if (EDIT_TOOLS.has(tool)) {
    return "edit";
  }
  if (tool === "Read") {
    return "read";
  }
  if (tool === "Bash") {
    return "command";
  }
  return "tool";
}

/** Extract the touched file path for file-family tools (FR-1.1/1.4). */
function extractFile(
  tool: string,
  input: Record<string, unknown>
): string | undefined {
  if (tool !== "Read" && !EDIT_TOOLS.has(tool)) {
    // Grep/Glob `path` is a search root, not a file the node touched.
    return undefined;
  }
  const fp = input.file_path ?? input.notebook_path ?? input.filePath ?? input.path;
  return typeof fp === "string" && fp ? fp : undefined;
}

/** Build the `detail` preview for a tool draft (FR-1.1: Bash → command line). */
function detailForTool(
  tool: string,
  input: Record<string, unknown>
): string | undefined {
  if (tool === "Bash") {
    const cmd = typeof input.command === "string" ? input.command : "";
    return cmd ? truncate(cmd, MAX_ACTION_DETAIL_CHARS) : undefined;
  }
  try {
    const str = JSON.stringify(input);
    if (!str || str === "{}") {
      return undefined;
    }
    return truncate(str, MAX_ACTION_DETAIL_CHARS);
  } catch {
    return undefined;
  }
}

/**
 * Turn one thinker/tool event into an {@link ActionDraft}, or `null` when the
 * event isn't a discrete action (thinking/content stream into the transcript,
 * not the log; a tool_result's tool_start already produced the row). PURE — no
 * run context, no side effects. The host stamps seq/at/nodeId on the result.
 */
export function classifyThinkerEvent(evt: ThinkerEventLike): ActionDraft | null {
  if (!evt || typeof evt !== "object") {
    return null;
  }
  const md = (evt.metadata ?? {}) as Record<string, unknown>;
  const text = typeof evt.text === "string" ? evt.text : "";

  if (evt.type === "tool_start") {
    const tool = typeof md.tool === "string" ? md.tool : undefined;
    if (!tool) {
      return null;
    }
    const input = (md.input ?? {}) as Record<string, unknown>;
    const kind = toolKind(tool);
    const file = extractFile(tool, input);
    const summary = truncate(
      text || `${tool}${file ? `: ${basename(file)}` : ""}`,
      MAX_ACTION_SUMMARY_CHARS
    );
    const detail = detailForTool(tool, input);
    const draft: ActionDraft = { kind, tool, summary };
    if (file) {
      draft.file = file;
    }
    if (detail) {
      draft.detail = detail;
    }
    return draft;
  }

  if (evt.type === "error") {
    const summary = truncate(text || "error", MAX_ACTION_SUMMARY_CHARS);
    const draft: ActionDraft = { kind: "error", summary };
    if (text) {
      draft.detail = truncate(text, MAX_ACTION_DETAIL_CHARS);
    }
    return draft;
  }

  // thinking / content → transcript, tool_result → covered by its tool_start,
  // usage / done / ask_user → not discrete actions.
  return null;
}

/**
 * Append a record to a node's list in place, enforcing the per-node FIFO cap
 * (FR-1.2) — oldest records drop first. Mirrors appendTrace's mutate-in-place
 * contract so host and webview converge on the identical list.
 */
export function pushAction(
  list: ActionRecord[],
  record: ActionRecord,
  cap: number = MAX_ACTIONS_PER_NODE
): void {
  list.push(record);
  while (list.length > cap) {
    list.shift();
  }
}

/**
 * Distinct file paths from a node's records, most-recent-first (FR-1.4).
 * `runningOnly` → files still being written (edit-kind only), capped to
 * {@link MAX_FILES_IN_PROGRESS} (filesInProgress for the running node).
 * Otherwise every touched file (edit + read) — filesTouched for a finished node.
 */
export function deriveFiles(
  records: ActionRecord[],
  opts: { runningOnly?: boolean; cap?: number } = {}
): string[] {
  const runningOnly = opts.runningOnly ?? false;
  const cap = opts.cap ?? (runningOnly ? MAX_FILES_IN_PROGRESS : 0);
  const seen = new Set<string>();
  const out: string[] = [];
  for (let i = records.length - 1; i >= 0; i--) {
    const r = records[i];
    if (!r.file) {
      continue;
    }
    if (runningOnly && r.kind !== "edit") {
      continue;
    }
    if (seen.has(r.file)) {
      continue;
    }
    seen.add(r.file);
    out.push(r.file);
    if (cap > 0 && out.length >= cap) {
      break;
    }
  }
  return out;
}

/** Compose-able filter for the log view (FR-4.3). All fields optional. */
export interface ActionFilter {
  nodeId?: string;
  kind?: ActionKind;
  tool?: string;
  /** Case-insensitive substring across summary/detail/file. */
  search?: string;
}

/**
 * Filter records by node/kind/tool and a free-text search (FR-4.3). Pure — a
 * new array; the log view's WU-4 table calls this directly. Filters compose
 * (every provided field must match).
 */
export function filterActions(
  records: ActionRecord[],
  filter: ActionFilter = {}
): ActionRecord[] {
  const q = filter.search?.trim().toLowerCase();
  return records.filter((r) => {
    if (filter.nodeId && r.nodeId !== filter.nodeId) {
      return false;
    }
    if (filter.kind && r.kind !== filter.kind) {
      return false;
    }
    if (filter.tool && r.tool !== filter.tool) {
      return false;
    }
    if (q) {
      const hay = `${r.summary} ${r.detail ?? ""} ${r.file ?? ""}`.toLowerCase();
      if (!hay.includes(q)) {
        return false;
      }
    }
    return true;
  });
}

/** Columns the log view sorts by (FR-4.2). */
export type ActionSortKey = "time" | "node" | "kind" | "tool" | "duration";

export interface ActionSort {
  by: ActionSortKey;
  dir?: "asc" | "desc";
}

/**
 * Sort records by a column (FR-4.2), returning a NEW array (never mutates the
 * source). `seq` breaks ties so the order is stable/deterministic. Pure — the
 * WU-4 table drives this from its header state.
 */
export function sortActions(
  records: ActionRecord[],
  sort: ActionSort
): ActionRecord[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  const copy = records.slice();
  copy.sort((a, b) => {
    let cmp = 0;
    switch (sort.by) {
      case "time":
        cmp = a.at - b.at;
        break;
      case "node":
        cmp = a.nodeId.localeCompare(b.nodeId);
        break;
      case "kind":
        cmp = a.kind.localeCompare(b.kind);
        break;
      case "tool":
        cmp = (a.tool ?? "").localeCompare(b.tool ?? "");
        break;
      case "duration":
        cmp = (a.durationMs ?? 0) - (b.durationMs ?? 0);
        break;
    }
    if (cmp === 0) {
      cmp = a.seq - b.seq;
    }
    return cmp * dir;
  });
  return copy;
}
